import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { returnsAPI, suppliersAPI, productsAPI, getApiErrorPayload } from "@/lib/api";
import { createReturnSchema } from "@/lib/schemas/return.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { SectionHead } from "@/components/ui/section-head";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError, firstFieldErrors } from "@/components/ui/field-error";

interface ReturnItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface ReturnForm {
	supplierId: string;
	returnDate: string;
	status: "pending" | "ordered" | "received";
	paymentStatus: "unpaid" | "partial" | "paid";
	taxAmount: number;
	notes: string;
}

const AddReturnPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { user } = useAuth();
	const currency = useCurrency();
	const [saving, setSaving] = useState(false);
	const [suppliers, setSuppliers] = useState<any[]>([]);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<ReturnItem[]>([]);
	const [returnForm, setReturnForm] = useState<ReturnForm>({
		supplierId: "",
		returnDate: new Date().toISOString().split("T")[0],
		status: "pending",
		paymentStatus: "unpaid",
		taxAmount: 0,
		notes: "",
	});
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const { isDirty } = useIsDirty({ returnForm, selectedProducts });
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		fetchSuppliers();
		fetchProducts();
	}, []);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const filtered = products.filter(
				product =>
					product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			setFilteredProducts(filtered);
		}
	}, [searchQuery, products]);

	const fetchSuppliers = async () => {
		try {
			const data = await suppliersAPI.getAll({ page: 1, limit: 1000 });
			setSuppliers(data.data || []);
		} catch (error) {
			console.error("Failed to fetch suppliers:", error);
		}
	};

	const fetchProducts = async () => {
		try {
			const data = await productsAPI.getAll({ page: 1, limit: 1000 });
			setProducts(data.data || []);
			setFilteredProducts(data.data || []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
		}
	};

	const handleInputChange = (field: keyof ReturnForm, value: any) => {
		setReturnForm(prev => ({ ...prev, [field]: value }));
		clearFieldError(field);
	};

	// Unified line math (matches the backend): discount = subtotal*discountRate/100;
	// tax = (subtotal - discount)*taxRate/100; total = subtotal - discount + tax.
	const recalcItem = (item: ReturnItem): ReturnItem => {
		const lineSubtotal = item.unitPrice * item.quantity;
		const discountAmount = (lineSubtotal * item.discountPercentage) / 100;
		const taxAmount = ((lineSubtotal - discountAmount) * item.taxPercentage) / 100;
		const totalAmount = lineSubtotal - discountAmount + taxAmount;
		return { ...item, discountAmount, taxAmount, totalAmount };
	};

	const addProductToReturn = (product: any) => {
		clearFieldError("items");
		const existingItem = selectedProducts.find(item => item.productId === product.id);

		if (existingItem) {
			setSelectedProducts(prev =>
				prev.map(item =>
					item.productId === product.id ? recalcItem({ ...item, quantity: item.quantity + 1 }) : item,
				),
			);
		} else {
			const newItem: ReturnItem = recalcItem({
				id: Date.now().toString(),
				productId: product.id,
				productName: product.name,
				productImage: product.image,
				barcode: product.barcode,
				quantity: 1,
				unitPrice: product.purchasePrice || 0,
				discountPercentage: 0,
				discountAmount: 0,
				taxPercentage: Number(product.taxRate) || 0,
				taxAmount: 0,
				totalAmount: product.purchasePrice || 0,
			});
			setSelectedProducts(prev => [...prev, newItem]);
		}

		setSearchQuery("");
		setFilteredProducts(products);
	};

	const updateItem = (itemId: string, field: keyof ReturnItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => (item.id === itemId ? recalcItem({ ...item, [field]: value }) : item)),
		);
	};

	const removeItem = (itemId: string) => {
		setSelectedProducts(prev => prev.filter(item => item.id !== itemId));
	};

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
		const totalDiscount = selectedProducts.reduce((sum, item) => sum + item.discountAmount, 0);
		const totalTax = selectedProducts.reduce((sum, item) => sum + item.taxAmount, 0);
		const total = subtotal - totalDiscount + totalTax;

		return { subtotal, totalDiscount, totalTax, total };
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!returnForm.supplierId) errors.supplierId = "returns.errors.supplierRequired";
		if (selectedProducts.length === 0) errors.items = "returns.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});

		try {
			setSaving(true);
			const itemsForApi = selectedProducts.map(item => ({
				productId: Number(item.productId),
				quantity: item.quantity,
				unitPrice: Number(item.unitPrice),
				discountPercentage: item.discountPercentage ?? 0,
				taxPercentage: item.taxPercentage ?? 0,
				notes: (item as any).notes,
			}));
			const returnData = {
				type: ((returnForm as any).type || "PURCHASE_RETURN") as "PURCHASE_RETURN" | "SALE_RETURN",
				supplierId: returnForm.supplierId ? Number(returnForm.supplierId) : undefined,
				returnDate: returnForm.returnDate,
				status: returnForm.status,
				paymentStatus: returnForm.paymentStatus,
				notes: returnForm.notes,
				discountAmount: (returnForm as any).discountAmount,
				shippingAmount: (returnForm as any).shippingAmount,
				items: itemsForApi,
				subtotal: calculateTotals().subtotal,
				totalAmount: calculateTotals().total,
			};

			const parsed = createReturnSchema.safeParse(returnData);
			if (!parsed.success) {
				setFieldErrors(firstFieldErrors(parsed.error.issues));
				toast({
					title: t("common.error"),
					description: t("validation.checkFields"),
					variant: "destructive",
				});
				return;
			}
			const toSend = { ...parsed.data } as any;
			if (user?.branchId != null) toSend.branchId = Number(user.branchId);

			await returnsAPI.create(toSend);
			toast({
				title: t("common.success"),
				description: t("returns.messages.created"),
			});
			allowNavigation();
			navigate("/returns");
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("returns.add.title")}</PageHeading>
						<p className="text-muted-foreground">{t("returns.add.description")}</p>
					</div>
				</div>
				<Button onClick={handleSubmit} disabled={saving}>
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("returns.add.save")}
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Return Form */}
				<div className="lg:col-span-2 space-y-6">
					{/* Operation Data */}
					<SectionHead num={1} title={t("returns.add.operationData")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								<div className="space-y-2">
									<Label>
										{t("returns.add.supplier")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={returnForm.supplierId}
										onValueChange={value => handleInputChange("supplierId", value)}
									>
										<SelectTrigger
											aria-label={t("returns.add.supplier")}
											aria-invalid={!!fieldErrors.supplierId}
										>
											<SelectValue placeholder={t("returns.add.selectSupplier")} />
										</SelectTrigger>
										<SelectContent>
											{suppliers.map(supplier => (
												<SelectItem key={supplier.id} value={String(supplier.id)}>
													{supplier.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.supplierId} />
								</div>
								<div className="space-y-2">
									<Label>{t("returns.add.returnDate")}</Label>
									<Input
										aria-label={t("returns.add.returnDate")}
										type="date"
										value={returnForm.returnDate}
										onChange={e => handleInputChange("returnDate", e.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<Label>{t("returns.add.status")}</Label>
									<Select
										value={returnForm.status}
										onValueChange={value => handleInputChange("status", value)}
									>
										<SelectTrigger aria-label={t("returns.add.status")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="pending">{t("returns.status.pending")}</SelectItem>
											<SelectItem value="ordered">{t("returns.status.ordered")}</SelectItem>
											<SelectItem value="received">{t("returns.status.received")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>{t("returns.add.paymentStatus")}</Label>
									<Select
										value={returnForm.paymentStatus}
										onValueChange={value => handleInputChange("paymentStatus", value)}
									>
										<SelectTrigger aria-label={t("returns.add.paymentStatus")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="unpaid">{t("returns.paymentStatus.unpaid")}</SelectItem>
											<SelectItem value="partial">
												{t("returns.paymentStatus.partial")}
											</SelectItem>
											<SelectItem value="paid">{t("returns.paymentStatus.paid")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>{t("returns.add.tax")}</Label>
									<Input
										aria-label={t("returns.add.tax")}
										type="number"
										min="0"
										step="0.01"
										value={returnForm.taxAmount}
										onChange={e => handleInputChange("taxAmount", parseFloat(e.target.value) || 0)}
										placeholder="0.00"
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label>{t("returns.add.notes")}</Label>
								<Input
									aria-label={t("returns.add.notes")}
									type="text"
									value={returnForm.notes}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("returns.add.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Products Selection */}
					<SectionHead
						num={2}
						title={t("returns.add.products")}
						hint={t("returns.add.productsDescription")}
					/>
					<Card>
						<CardContent className="space-y-6 pt-6">
							{/* Product Search */}
							<div className="space-y-2">
								<Label>{t("returns.add.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("returns.add.searchProducts")}
										placeholder={t("returns.add.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							{/* Product list: all when search empty, filtered when typing */}
							<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
									{filteredProducts.map(product => (
										<div
											key={product.id}
											className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
											onClick={() => addProductToReturn(product)}
										>
											<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
												{product.image ? (
													<img
														src={product.image}
														alt={product.name}
														className="w-full h-full object-cover rounded-lg"
													/>
												) : (
													<Package className="h-6 w-6 text-primary" />
												)}
											</div>
											<div className="flex-1">
												<h4 className="font-medium">{product.name}</h4>
												<p className="text-sm text-muted-foreground">
													{product.barcode &&
														`${t("products.table.barcode")}: ${product.barcode}`}
												</p>
												<p className="text-sm font-medium">
													{t("returns.add.price")}:{" "}
													<Price value={product.purchasePrice || 0} currency={currency} />
												</p>
											</div>
											<Button
												title={t("common.add")}
												aria-label={t("common.add")}
												size="sm"
												variant="outline"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</div>

							{/* Selected Products */}
							<div className="space-y-4">
								<h4 className="font-medium">
									{t("returns.add.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
										<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
											{item.productImage ? (
												<img
													src={item.productImage}
													alt={item.productName}
													className="w-full h-full object-cover rounded-lg"
												/>
											) : (
												<Package className="h-8 w-8 text-primary" />
											)}
										</div>
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-4 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("returns.add.quantity")}</Label>
													<Input
														aria-label={t("returns.add.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.add.unitPrice")}</Label>
													<Input
														aria-label={t("returns.add.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.add.discount")}</Label>
													<Input
														aria-label={t("returns.add.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.add.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Summary */}
				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("returns.add.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("returns.add.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("returns.add.discount")}</span>
								<span className="font-semibold text-destructive">
									{totalDiscount > 0 && "-"}
									<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("returns.add.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("returns.add.total")}</span>
									<Price value={total} currency={currency} />
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Sticky action bar */}
			<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
				<Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saving}>
					{t("common.cancel")}
				</Button>
				<Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-40">
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("common.save")}
				</Button>
			</div>
		</div>
	);
};

export default AddReturnPage;
